<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { useProductPageStore } from "~/stores/main";

type ProductProperty = {
  index_code: string;
  name: string;
};

const hideProperties = [
  "masterproduct_dont_degroup_at_order_property",
  "masterproduct_created_property",
  "product_szin_property",
  "product_paletta_property",
  "product_meret_property",
];

// -----------------------
// props
// -----------------------
const props = withDefaults(
  defineProps<{
    store?: any;
  }>(),
  {
    store: null,
  },
);

// -----------------------
// composables
// -----------------------
const store = props.store || useProductPageStore();
const { t } = useI18n();

// -----------------------
// computed properties
// -----------------------
const propertiesRef = computed(() => {
  const properties: Record<string, string> = {};

  if (Array.isArray(allProperties) && allProperties.length) {
    for (const property of allProperties) {
      if (
        store.masterProduct &&
        property.index_code in store.masterProduct &&
        !hideProperties.includes(property.index_code)
      ) {
        const label = property.name;
        const value = String(store.masterProduct[property.index_code]);
        properties[label] = value
          .replace(/^\|+|\|+$/g, "")
          .replace(/\|/g, ", ");
      }
    }
  }

  return properties;
});

const hasAttributes = computed(() => {
  return Object.keys(propertiesRef.value).length > 0;
});

let allProperties: ProductProperty[] | null = null;

try {
  allProperties = await $fetch(`/api/meilisearch/properties`, {
    method: "POST",
    body: {
      limit: 200,
    },
  });
} catch (error) {
  Sentry.captureEvent({
    message: `Failed to fetch properties`,
    level: "error",
    extra: {
      key: "ER-3MN8",
      error,
    },
  });
}
</script>

<template>
  <div v-if="hasAttributes" class="col-span-full px-2.5 text-brand-headline">
    <h2
      class="font-bold font-lora !text-2xl lg:!text-3xl text-center h-[60px] !leading-[60px] !mt-0"
    >
      {{ t("technical_details") }}
    </h2>

    <div
      class="w-full mt-9 text-base grid grid-cols-[auto_1fr] leading-[36px] lg:leading-[34px]"
    >
      <template v-for="(value, label) in propertiesRef" :key="label">
        <div class="font-bold pr-[40px]">
          {{ label }}
        </div>
        <div>
          {{ value }}
        </div>
      </template>
    </div>
  </div>
</template>
